import React, { useEffect, useMemo, useState } from "react";
import { AccountBalance, Add, Payments, Save, SwapHoriz } from "@mui/icons-material";
import { createAccount, createTransaction, fetchAccounts, fetchTransactions } from "../services/owdedApi";
import { dateOnly, money } from "./format";
import { EmptyState, LoadingOverlay, Modal, Pagination, StatusPill } from "./ui";
import { paginateRows, PAGE_SIZE } from "./listUtils";

const initialAccount = {
  account_type: "CASH",
  name: "",
  currency: "CDF",
  opening_balance: 0,
  bank_name: "",
  account_number: "",
  status: "ACTIVE",
};

const initialTransaction = {
  transaction_type: "INFLOW",
  account_id: "",
  from_account_id: "",
  to_account_id: "",
  category: "ADJUSTMENT",
  amount: 0,
  currency: "CDF",
  transaction_date: new Date().toISOString().slice(0, 10),
  description: "",
};

const transactionLabels = {
  INFLOW: "Entree",
  OUTFLOW: "Sortie",
  TRANSFER: "Transfert",
};

const categoryLabels = {
  INVESTMENT_DISBURSEMENT: "Decaissement investissement",
  RECOVERY: "Recouvrement",
  FUND_RECEIVED: "Fonds recu",
  BANK_DEPOSIT: "Depot bancaire",
  BANK_WITHDRAWAL: "Retrait bancaire",
  OPERATING_EXPENSE: "Depense operationnelle",
  ADJUSTMENT: "Ajustement",
  TRANSFER: "Transfert",
};

function TreasuryPage() {
  const [accounts, setAccounts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [search, setSearch] = useState("");
  const [currency, setCurrency] = useState("ALL");
  const [page, setPage] = useState(1);
  const [modal, setModal] = useState(null);
  const [accountForm, setAccountForm] = useState(initialAccount);
  const [transactionForm, setTransactionForm] = useState(initialTransaction);
  const [notice, setNotice] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    const [accountRows, transactionRows] = await Promise.all([fetchAccounts(), fetchTransactions()]);
    setAccounts(accountRows || []);
    setTransactions(transactionRows || []);
  };

  useEffect(() => { load().catch(() => setNotice("Impossible de charger la tresorerie.")); }, []);
  useEffect(() => { setPage(1); }, [search, currency]);

  const totals = useMemo(() => accounts.reduce((result, account) => {
    const code = account.currency || "CDF";
    if (!result[code]) result[code] = { cash: 0, bank: 0 };
    const isBank = account.account_type === "BANK" || String(account.name || "").toLowerCase().includes("bancaire");
    result[code][isBank ? "bank" : "cash"] += Number(account.current_balance || 0);
    return result;
  }, {}), [accounts]);

  const filteredTransactions = useMemo(() => {
    const term = search.trim().toLowerCase();
    return transactions.filter((row) => {
      const matchesCurrency = currency === "ALL" || row.currency === currency || row.account_currency === currency;
      const matchesSearch = [row.account_name, row.description, row.category, row.transaction_type, row.reference_type]
        .filter(Boolean).join(" ").toLowerCase().includes(term);
      return matchesCurrency && matchesSearch;
    });
  }, [currency, search, transactions]);

  const pagination = useMemo(() => paginateRows(filteredTransactions, page, PAGE_SIZE), [filteredTransactions, page]);

  const submitAccount = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      await createAccount(accountForm);
      setAccountForm(initialAccount);
      setModal(null);
      setNotice("Compte cree avec succes.");
      await load();
    } catch (err) {
      setNotice(err.response?.data?.error || "Creation du compte impossible.");
    } finally {
      setLoading(false);
    }
  };

  const submitTransaction = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      await createTransaction(transactionForm);
      setTransactionForm(initialTransaction);
      setModal(null);
      setNotice("Operation enregistree avec succes.");
      await load();
    } catch (err) {
      setNotice(err.response?.data?.error || "Enregistrement de l operation impossible.");
    } finally {
      setLoading(false);
    }
  };

  const updateTransaction = (field, value) => {
    setTransactionForm((current) => {
      const next = { ...current, [field]: value };
      if (field === "transaction_type" && value === "TRANSFER") {
        next.category = "TRANSFER";
        next.account_id = "";
      }
      if (field === "transaction_type" && value !== "TRANSFER" && current.category === "TRANSFER") {
        next.category = "ADJUSTMENT";
        next.from_account_id = "";
        next.to_account_id = "";
      }
      if (field === "account_id" || field === "from_account_id") {
        const account = accounts.find((item) => String(item.id) === String(value));
        if (account) next.currency = account.currency;
      }
      return next;
    });
  };

  const transferTargets = accounts.filter((account) => account.status === "ACTIVE" && account.currency === transactionForm.currency && String(account.id) !== String(transactionForm.from_account_id));

  return (
    <main className="page-content management-page">
      <div className="page-head"><div><h1>Tresorerie</h1><p>Comptes de caisse, comptes bancaires et journal des operations.</p></div></div>
      {notice && <p className="notice">{notice}</p>}
      
          <br/>
      <section className="treasury-create-grid">
        <article className="panel treasury-create-card"><div className="treasury-create-card__icon"><AccountBalance /></div><div><span>Gestion des comptes</span><h2>Nouveau compte</h2><p>Creer une caisse ou un compte bancaire dans sa devise.</p></div><button className="ghost-btn" type="button" onClick={() => setModal("account")}><Add /> Creer le compte</button></article>
        <article className="panel treasury-create-card is-operation"><div className="treasury-create-card__icon"><Payments /></div><div><span>Journal financier</span><h2>Nouvelle operation</h2><p>Enregistrer une entree, une sortie ou un transfert entre comptes.</p></div><button className="primary-btn" type="button" onClick={() => setModal("transaction")}><SwapHoriz /> Enregistrer</button></article>
      </section>
          <br/>

      <section className="treasury-balance-grid">
        {["CDF", "USD"].map((code) => <React.Fragment key={code}><article className="mini-card"><span>Caisse {code}</span><strong>{money(totals[code]?.cash, code)}</strong></article><article className="mini-card"><span>Banque {code}</span><strong>{money(totals[code]?.bank, code)}</strong></article></React.Fragment>)}
      </section>
          <br/>

      <section className="panel data-panel">
        <div className="table-toolbar"><div><h2 className="section-title">Comptes actifs</h2><small>{accounts.length} compte(s)</small></div></div>
        {accounts.length ? <div className="table-scroll"><table className="data-table"><thead><tr><th>Compte</th><th>Type</th><th>Devise</th><th>Banque / numero</th><th>Solde</th><th>Statut</th></tr></thead><tbody>{accounts.map((account) => <tr key={account.id}><td>{account.name}</td><td>{account.account_type === "BANK" ? "Banque" : "Caisse"}</td><td>{account.currency}</td><td>{account.bank_name || "-"}<br /><small>{account.account_number || "-"}</small></td><td>{money(account.current_balance, account.currency)}</td><td><StatusPill value={account.status} /></td></tr>)}</tbody></table></div> : <EmptyState />}
      </section>
          <br/>

      <section className="panel data-panel">
        <div className="table-toolbar"><div><h2 className="section-title">Journal caisse / banque</h2><small>{filteredTransactions.length} operation(s)</small></div><input placeholder="Recherche avancee..." value={search} onChange={(event) => setSearch(event.target.value)} /><select value={currency} onChange={(event) => setCurrency(event.target.value)}><option value="ALL">Toutes les devises</option><option value="CDF">CDF</option><option value="USD">USD</option></select></div>
        {filteredTransactions.length ? <div className="table-scroll"><table className="data-table"><thead><tr><th>Date</th><th>Compte</th><th>Operation</th><th>Rubrique</th><th>Description</th><th>Devise</th><th>Montant</th><th>Statut</th></tr></thead><tbody>{pagination.rows.map((row) => <tr key={row.id}><td>{dateOnly(row.transaction_date)}</td><td>{row.account_name}</td><td>{transactionLabels[row.transaction_type] || row.transaction_type}</td><td>{categoryLabels[row.category] || row.category}</td><td>{row.description || "-"}</td><td>{row.currency || row.account_currency}</td><td>{money(row.amount, row.currency || row.account_currency)}</td><td><StatusPill value={row.status} /></td></tr>)}</tbody></table></div> : <EmptyState />}
        <Pagination page={pagination.safePage} totalPages={pagination.totalPages} totalItems={filteredTransactions.length} from={pagination.from} to={pagination.to} onPageChange={setPage} />
      </section>

      {modal === "account" && <Modal title="Nouveau compte" onClose={() => setModal(null)} footer={<button className="primary-btn" form="account-form"><Save /> Enregistrer</button>}><form id="account-form" onSubmit={submitAccount}><div className="form-grid"><label>Type<select value={accountForm.account_type} onChange={(event) => setAccountForm({ ...accountForm, account_type: event.target.value })}><option value="CASH">Caisse</option><option value="BANK">Banque</option></select></label><label>Devise<select value={accountForm.currency} onChange={(event) => setAccountForm({ ...accountForm, currency: event.target.value })}><option value="CDF">CDF</option><option value="USD">USD</option></select></label><label className="full-row">Nom du compte<input value={accountForm.name} onChange={(event) => setAccountForm({ ...accountForm, name: event.target.value })} required /></label><label>Solde d ouverture<input type="number" min="0" value={accountForm.opening_balance} onChange={(event) => setAccountForm({ ...accountForm, opening_balance: event.target.value })} /></label>{accountForm.account_type === "BANK" && <><label>Banque<input value={accountForm.bank_name} onChange={(event) => setAccountForm({ ...accountForm, bank_name: event.target.value })} /></label><label className="full-row">Numero du compte<input value={accountForm.account_number} onChange={(event) => setAccountForm({ ...accountForm, account_number: event.target.value })} /></label></>}</div></form></Modal>}

      {modal === "transaction" && <Modal title="Nouvelle operation" onClose={() => setModal(null)} footer={<button className="primary-btn" form="transaction-form"><Save /> Valider l operation</button>}><form id="transaction-form" onSubmit={submitTransaction}><div className="form-grid"><label>Operation<select value={transactionForm.transaction_type} onChange={(event) => updateTransaction("transaction_type", event.target.value)}><option value="INFLOW">Entree</option><option value="OUTFLOW">Sortie</option><option value="TRANSFER">Transfert</option></select></label><label>Montant<input type="number" min="1" value={transactionForm.amount} onChange={(event) => updateTransaction("amount", event.target.value)} required /></label>{transactionForm.transaction_type === "TRANSFER" ? <><label>Compte de debit<select value={transactionForm.from_account_id} onChange={(event) => updateTransaction("from_account_id", event.target.value)} required><option value="">Selectionner</option>{accounts.filter((account) => account.status === "ACTIVE").map((account) => <option key={account.id} value={account.id}>{account.name} - {money(account.current_balance, account.currency)}</option>)}</select></label><label>Compte de credit<select value={transactionForm.to_account_id} onChange={(event) => updateTransaction("to_account_id", event.target.value)} required><option value="">Selectionner</option>{transferTargets.map((account) => <option key={account.id} value={account.id}>{account.name}</option>)}</select></label></> : <><label>Compte concerne<select value={transactionForm.account_id} onChange={(event) => updateTransaction("account_id", event.target.value)} required><option value="">Selectionner</option>{accounts.filter((account) => account.status === "ACTIVE").map((account) => <option key={account.id} value={account.id}>{account.name} - {money(account.current_balance, account.currency)}</option>)}</select></label><label>Rubrique<select value={transactionForm.category} onChange={(event) => updateTransaction("category", event.target.value)}>{Object.entries(categoryLabels).filter(([key]) => key !== "TRANSFER").map(([key, label]) => <option key={key} value={key}>{label}</option>)}</select></label></>}<label>Date<input type="date" value={transactionForm.transaction_date} onChange={(event) => updateTransaction("transaction_date", event.target.value)} /></label><label className="full-row">Description<textarea value={transactionForm.description} onChange={(event) => updateTransaction("description", event.target.value)} /></label></div></form></Modal>}
      <LoadingOverlay show={loading} />
    </main>
  );
}

export default TreasuryPage;
